import React, { useState, useMemo } from "react";
import { Tabs, Collapse, Typography, Table, message } from "antd";
import {
  CheckCircle,
  ArrowRight,
  Code,
  Shield,
  Database,
  Clock,
  Download,
  Lock,
  BookOpen,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import SDKDownloadModal from "../components/SDKDownloadModal";
import useUserStore from "../store/userStore";
import { apiService } from "../api/apiService";
import { useTranslation } from "../i18n/useTranslation";

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const API = () => {
  const { isLoggedIn } = useUserStore();
  const { t, language } = useTranslation();
  const [isSdkModalVisible, setIsSdkModalVisible] = useState(false);
  const navigate = useNavigate();

  // API 示例数据 - 使用翻译函数动态生成
  const apiCategories = useMemo(() => [
  {
    id: "auth",
    name: t("apiCategoryAuthName"),
    description: t("apiCategoryAuthDesc"),
    endpoints: [
      {
        id: "auth-register",
        method: "POST",
        path: "/api/register",
        summary: t("apiEndpointRegisterSummary"),
        description: t("apiEndpointRegisterDesc"),
        request: {
          email: "string (email)",
          phone: "string (1[3-9]\\d{9})",
          password: "string (minLength 8)",
          confirm_password: "string",
          company_name: "string",
        },
        response: { success: "boolean", message: "string" },
        example: {
          request: `curl -X POST "https://www.stabilize-ai.com/api/register" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@company.com",
    "phone": "13800138000",
    "password": "MyPass123",
    "confirm_password": "MyPass123",
    "company_name": "未来科技有限公司"
  }'`,
          response: `{
  "success": true,
  "message": "操作成功"
}`,
        },
      },
      {
        id: "auth-login",
        method: "POST",
        path: "/api/login",
        summary: t("apiEndpointLoginSummary"),
        description: t("apiEndpointLoginDesc"),
        request: {
          email_or_phone: "string (邮箱或手机号)",
          password: "string",
        },
        response: { success: "boolean", message: "string", token: "string (UUID)" },
        example: {
          request: `curl -X POST "https://www.stabilize-ai.com/api/login" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email_or_phone": "user@company.com",
    "password": "MyPass123"
  }'`,
          response: `{
  "success": true,
  "message": "登录成功",
  "token": "a1b2c3d4-5678-90ef-ghij-klmnopqrstuv"
}`,
        },
      },
      {
        id: "auth-forgot-password",
        method: "POST",
        path: "/api/forgot-password",
        summary: t("apiEndpointForgotPasswordSummary"),
        description: t("apiEndpointForgotPasswordDesc"),
        request: {
          email_or_phone: "string",
          new_password: "string",
        },
        response: { success: "boolean", message: "string" },
        example: {
          request: `curl -X POST "https://www.stabilize-ai.com/api/forgot-password" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email_or_phone": "user@company.com",
    "new_password": "NewPass456"
  }'`,
          response: `{
  "success": true,
  "message": "操作成功"
}`,
        },
      },
      {
        id: "auth-logout",
        method: "POST",
        path: "/api/logout",
        summary: t("apiEndpointLogoutSummary"),
        description: t("apiEndpointLogoutDesc"),
        request: null,
        response: { success: "boolean", message: "string" },
        example: {
          request: `curl -X POST "https://www.stabilize-ai.com/api/logout" \\
  -H "Authorization: Bearer <token>"`,
          response: `{
  "success": true,
  "message": "操作成功"
}`,
        },
      },
    ],
  },
  {
    id: "ai",
    name: t("apiCategoryAIServiceName"),
    description: t("apiCategoryAIServiceDesc"),
    endpoints: [
      {
        id: "ai-inference",
        method: "TCP",
        path: "127.0.0.1:9000",
        summary: t("apiEndpointInferenceSummary"),
        description: t("apiEndpointInferenceDesc"),
        request: null,
        response: {
          login: { status: "ok", token: "string" },
          resume: { status: "ok", message: "resume set" },
          inference: { status: "success", action: "number[]" },
          logout: { status: "ok" },
        },
        example: {
          request: `# client_request_example.py
import socket
import json
import numpy as np

HOST = "127.0.0.1"
PORT = 9000

def run_example(username, password, resume_flag=False, n_requests=1):
    s = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
    s.connect((HOST, PORT))
    fp = s.makefile("rwb")

    # 1) login
    login_msg = {"login": {"username": username, "password": password}}
    fp.write(json.dumps(login_msg).encode() + b"\\n")
    fp.flush()
    resp = fp.readline()
    r = json.loads(resp.decode())
    if r.get("status") != "ok":
        s.close()
        return
    token = r.get("token")

    # 2) resume (one-time)
    fp.write(json.dumps({"resume": bool(resume_flag)}).encode() + b"\\n")
    fp.flush()
    fp.readline()

    # 3) inference (repeatable)
    for i in range(n_requests):
        obs = np.zeros(18).tolist()
        feedback = np.zeros(1).tolist()
        req = {"obs": obs, "feedback": feedback}
        fp.write(json.dumps(req).encode() + b"\\n")
        fp.flush()
        resp = fp.readline()
        data = json.loads(resp.decode())
        print("response:", data)

    # 4) logout (optional)
    fp.write(json.dumps({"cmd": "logout"}).encode() + b"\\n")
    fp.flush()
    fp.readline()

    s.close()

if __name__ == "__main__":
    run_example("admin", "admin123", resume_flag=False, n_requests=10)`,
          response: `# 1) Login response
{"status": "ok", "token": "..."}

# 2) Resume response
{"status": "ok", "message": "resume set"}

# 3) Inference response (each request)
{"status": "success", "action": [0.0, 0.1, ...]}

# 4) Logout response
{"status": "ok"}`,
        },
      },
      {
        id: "ai-analyze",
        method: "POST",
        path: "/api/v1/ai/analyze",
        summary: t("apiEndpointAnalyzeSummary"),
        description: t("apiEndpointAnalyzeDesc"),
        request: {
          body: `cd your_current_path/dist/socket_test_arm
                ./socket_test_arm`,
        },
        response: {
          analysisId: "string",
          results: [
            {
              type: "alert",
              message: "Temperature is above threshold",
              severity: "high",
            },
          ],
          recommendations: [
            {
              action: "reduce_power",
              description: "Reduce device power consumption",
            },
          ],
        },
        example: {
          request: `curl -X POST "/api/v1/ai/analyze" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." \
  -H "Content-Type: application/json" \
  -d '{"deviceId": "device001", "data": {"temperature": 85, "humidity": 45, "pressure": 1013}}'`,
          response: `{
  "analysisId": "analysis-123",
  "results": [
    {
      "type": "alert",
      "message": "Temperature is above threshold",
      "severity": "high"
    }
  ],
  "recommendations": [
    {
      "action": "reduce_power",
      "description": "Reduce device power consumption"
    }
  ]
}`,
        },
      },
    ],
  },
  {
    id: "devices",
    name: t("apiCategoryDevicesName"),
    description: t("apiCategoryDevicesDesc"),
    endpoints: [
      {
        id: "devices-list",
        method: "GET",
        path: "/api/v1/devices",
        summary: t("apiEndpointDevicesListSummary"),
        description: t("apiEndpointDevicesListDesc"),
        response: {
          total: "number",
          devices: [
            {
              id: "device001",
              name: "Smart Thermostat",
              status: "online",
              lastSeen: "string",
            },
          ],
        },
        example: {
          request: `curl -X GET "/api/v1/devices" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."`,
          response: `{
  "total": 1,
  "devices": [
    {
      "id": "device001",
      "name": "Smart Thermostat",
      "status": "online",
      "lastSeen": "2024-01-01T12:00:00Z"
    }
  ]
}`,
        },
      },
      {
        id: "devices-detail",
        method: "GET",
        path: "/api/v1/devices/{deviceId}",
        summary: t("apiEndpointDeviceDetailSummary"),
        description: t("apiEndpointDeviceDetailDesc"),
        response: {
          id: "string",
          name: "string",
          status: "string",
          lastSeen: "string",
          specifications: {
            model: "string",
            firmwareVersion: "string",
          },
        },
        example: {
          request: `curl -X GET "/api/v1/devices/device001" \
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."`,
          response: `{
  "id": "device001",
  "name": "Smart Thermostat",
  "status": "online",
  "lastSeen": "2024-01-01T12:00:00Z",
  "specifications": {
    "model": "ST-1000",
    "firmwareVersion": "1.2.3"
  }
}`,
        },
      },
    ],
  },
], [t]);

  // 错误码数据 - 使用翻译函数动态生成
  const errorCodes = useMemo(() => [
    { code: 400, message: "Bad Request", description: t("apiError400Desc") },
    { code: 401, message: "Unauthorized", description: t("apiError401Desc") },
    { code: 403, message: "Forbidden", description: t("apiError403Desc") },
    { code: 404, message: "Not Found", description: t("apiError404Desc") },
    {
      code: 500,
      message: "Internal Server Error",
      description: t("apiError500Desc"),
    },
    { code: 502, message: "Bad Gateway", description: t("apiError502Desc") },
    { code: 503, message: "Service Unavailable", description: t("apiError503Desc") },
  ], [t]);

  // 认证方式数据 - 使用翻译函数动态生成
  const authMethods = useMemo(() => [
    {
      name: t("apiAuthMethod1Name"),
      description: "",
      usage: t("apiAuthMethod1Usage"),
      security: "medium",
      flowSteps: [
        t("apiAuthMethod1Step1"),
        t("apiAuthMethod1Step2"),
        t("apiAuthMethod1Step3"),
      ],
    },
    {
      name: t("apiAuthMethod2Name"),
      description: t("apiAuthMethod2Desc"),
      security: "high",
      flowSteps: [
        t("apiAuthMethod2Step1"),
        t("apiAuthMethod2Step2"),
        t("apiAuthMethod2Step3"),
        t("apiAuthMethod2Step4"),
        t("apiAuthMethod2Step5"),
      ],
    },
  ], [t]);

  const handleDownloadSDK = () => {
    if (!isLoggedIn) {
      navigate(`/login?redirect=${encodeURIComponent("/api-doc")}`);
      return;
    }
    setIsSdkModalVisible(true);
  };

  const handleModalClose = () => {
    setIsSdkModalVisible(false);
  };

  const handleViewGuide = () => {
    const guideFile = language === "en" ? "/SDK_USER_GUIDE_en.html" : "/SDK_USER_GUIDE.html";
    window.open(guideFile, "_blank");
  };

  const handleSdkDownload = async (platform) => {
    if (platform !== "Linux") {
      message.info(t("apiSDKNotAvailable", { platform }));
      return;
    }

    try {
      const response = await apiService.getSdkDownloadLink();
      const downloadUrl = response?.data?.download_url;

      if (!response?.success || !downloadUrl) {
        throw new Error(response?.message || t("sdkDownloadLinkMissing"));
      }

      window.open(downloadUrl, "_blank", "noopener");
      message.success(t("apiSDKDownloadSuccess", { platform }));
    } catch (error) {
      console.error("SDK下载失败:", error);
      if (error?.response?.status === 401) {
        navigate(`/login?redirect=${encodeURIComponent("/api-doc")}`);
        return;
      }
      const errorMessage =
        error?.response?.data?.message || error.message || t("apiSDKRetryLater");
      message.error(t("apiSDKDownloadError", { error: errorMessage }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航/英雄区 */}
      <section className="bg-gradient-to-b from-blue-300 to-indigo-100 py-16 pt-20 pb-10">
        <div className="container mx-auto px-4 pb-4">
          <div className="max-w-5xl mx-auto text-center">
            <Title
              level={1}
              className="mb-4 text-3xl md:text-4xl font-bold text-slate-800"
            >
              {t("apiHeroTitle")}
            </Title>
            <div className="text-gray-600 py-6 text-base md:text-lg max-w-3xl mx-auto">
              {t("apiHeroDesc")}
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-2 mb-6">
              <Button
                variant="blue-griadient"
                size="lg"
                onClick={handleDownloadSDK}
              >
                <Download className="mr-2 h-5 w-5" />
                {t("apiDownloadSDK")}
              </Button>
              <Button variant="secondary" size="lg" onClick={handleViewGuide}>
                <Code className="mr-2 h-5 w-5" />
                {t("apiViewDocs")}
              </Button>
            </div>

            {/* SDK下载弹窗 */}
            <SDKDownloadModal
              visible={isSdkModalVisible}
              onClose={handleModalClose}
              onDownload={handleSdkDownload}
            />
          </div>
        </div>
      </section>

      {/* API概览 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-wrap -mx-4">
              <div className="w-full md:w-1/3 px-4 mb-8 md:mb-0">
                <div className="h-full p-8 bg-blue-50 rounded-lg">
                  <div className="inline-flex h-16 w-16 mb-6 items-center justify-center text-white bg-blue-600 rounded-lg">
                    <Database className="w-8 h-8" />
                  </div>
                  <h3 className="mb-4 text-lg font-bold text-slate-800">
                    {t("apiOverviewTitle1")}
                  </h3>
                  <p className="text-slate-500">
                    {t("apiOverviewDesc1")}
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/3 px-4 mb-8 md:mb-0">
                <div className="h-full p-8 bg-blue-50 rounded-lg">
                  <div className="inline-flex h-16 w-16 mb-6 items-center justify-center text-white bg-blue-600 rounded-lg">
                    <Shield className="w-8 h-8" />
                  </div>
                  <h3 className="mb-4 text-lg font-bold text-slate-800">
                    {t("apiOverviewTitle2")}
                  </h3>
                  <p className="text-slate-500">
                    {t("apiOverviewDesc2")}
                  </p>
                </div>
              </div>
              <div className="w-full md:w-1/3 px-4">
                <div className="h-full p-8 bg-blue-50 rounded-lg">
                  <div className="inline-flex h-16 w-16 mb-6 items-center justify-center text-white bg-blue-600 rounded-lg">
                    <Clock className="w-8 h-8" />
                  </div>
                  <h3 className="mb-4 text-lg font-bold text-slate-800">
                    {t("apiOverviewTitle3")}
                  </h3>
                  <p className="text-slate-500">
                    {t("apiOverviewDesc3")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 主体内容 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* 认证方式 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <Lock className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">{t("apiAuthMethodsTitle")}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {authMethods.map((method, index) => (
                  <div
                    key={index}
                    className="p-6 bg-white rounded-lg shadow-sm border border-slate-200"
                  >
                    <div className="mb-3">
                      <h3 className="font-bold text-slate-800">
                        {method.name}
                      </h3>
                    </div>
                    {method.flowSteps ? (
                      <div className="space-y-3">
                        {method.usage && (
                          <div className="p-3 bg-slate-50 rounded text-sm font-mono text-slate-700 mb-3">
                            {method.usage}
                          </div>
                        )}
                        <ol className="list-decimal list-inside space-y-2 text-sm text-slate-600 pl-2">
                          {method.flowSteps.map((step, stepIndex) => (
                            <li key={stepIndex} className="leading-relaxed">
                              {step}
                            </li>
                          ))}
                        </ol>
                      </div>
                    ) : (
                      <>
                        <p className="text-slate-500 mb-3">
                          {method.description}
                        </p>
                        <div className="p-3 bg-slate-50 rounded text-sm font-mono text-slate-700">
                          {method.usage}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* API 端点 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">{t("apiEndpointsTitle")}</h2>
              </div>
              <Tabs defaultActiveKey="auth" type="card" className="mb-8">
                {apiCategories.map((category) => (
                  <Tabs.TabPane key={category.id} tab={category.name}>
                    <div className="mb-6">
                      <Text type="secondary">{category.description}</Text>
                    </div>
                    <Collapse defaultActiveKey={[category.endpoints[0].id]}>
                      {category.endpoints.map((endpoint) => (
                        <Panel
                          key={endpoint.id}
                          header={
                            <div className="flex items-center justify-between w-full">
                              <div>
                                <span
                                  className={`inline-block px-3 py-1 text-xs font-bold rounded-full mr-3 ${
                                    endpoint.method === "GET"
                                      ? "bg-green-100 text-green-800"
                                      : endpoint.method === "POST"
                                      ? "bg-blue-100 text-blue-800"
                                      : endpoint.method === "TCP"
                                      ? "bg-slate-100 text-slate-800"
                                      : "bg-purple-100 text-purple-800"
                                  }`}
                                >
                                  {endpoint.method}
                                </span>
                                <strong>{endpoint.path}</strong>
                              </div>
                              <span className="text-sm text-slate-500">
                                {endpoint.summary}
                              </span>
                            </div>
                          }
                        >
                          <div className="space-y-6">
                            <div>
                              <h4 className="font-bold text-slate-800 mb-2">
                                {t("apiEndpointDescription")}
                              </h4>
                              <p className="text-slate-600">
                                {endpoint.description}
                              </p>
                            </div>

                            {endpoint.request != null && (
                              <div>
                                <h4 className="font-bold text-slate-800 mb-2">
                                  {t("apiEndpointStartCommand")}
                                </h4>
                                <div className="bg-slate-50 p-4 rounded-lg">
                                  <pre className="text-sm font-mono text-slate-700 overflow-x-auto">
                                    {typeof endpoint.request === 'string' ? endpoint.request : JSON.stringify(endpoint.request, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            )}

                            {endpoint.response != null && (
                              <div>
                                <h4 className="font-bold text-slate-800 mb-2">
                                  {t("apiEndpointResponseFormat")}
                                </h4>
                                <div className="bg-slate-50 p-4 rounded-lg">
                                  <pre className="text-sm font-mono text-slate-700 overflow-x-auto">
                                    {typeof endpoint.response === 'string' ? endpoint.response : JSON.stringify(endpoint.response, null, 2)}
                                  </pre>
                                </div>
                              </div>
                            )}

                            <div>
                              <h4 className="font-bold text-slate-800 mb-2">
                                {t("apiEndpointExampleCode")}
                              </h4>
                              <Tabs defaultActiveKey="request">
                                <Tabs.TabPane tab={t("apiEndpointTabRequest")} key="request">
                                  <div className="bg-slate-900 p-4 rounded-lg">
                                    <pre className="text-sm text-slate-200 overflow-x-auto">
                                      {endpoint.example.request}
                                    </pre>
                                  </div>
                                </Tabs.TabPane>
                                <Tabs.TabPane tab={t("apiEndpointTabResponse")} key="response">
                                  <div className="bg-slate-900 p-4 rounded-lg">
                                    <pre className="text-sm text-slate-200 overflow-x-auto">
                                      {endpoint.example.response}
                                    </pre>
                                  </div>
                                </Tabs.TabPane>
                              </Tabs>
                            </div>
                          </div>
                        </Panel>
                      ))}
                    </Collapse>
                  </Tabs.TabPane>
                ))}
              </Tabs>
            </div>

            {/* 错误码 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <CheckCircle className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">{t("apiErrorCodesTitle")}</h2>
              </div>
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                <Table
                  dataSource={errorCodes}
                  columns={[
                    {
                      title: t("apiErrorCodeColumn1"),
                      dataIndex: "code",
                      key: "code",
                      width: 100,
                    },
                    { title: t("apiErrorCodeColumn2"), dataIndex: "message", key: "message" },
                    {
                      title: t("apiErrorCodeColumn3"),
                      dataIndex: "description",
                      key: "description",
                    },
                  ]}
                  pagination={false}
                  size="middle"
                />
              </div>
            </div>

            {/* 使用指南 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <ArrowRight className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">{t("apiQuickStartTitle")}</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="p-6 bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    1
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3">{t("apiQuickStartStep1Title")}</h3>
                  <p className="text-slate-500">
                    {t("apiQuickStartStep1Desc")}
                  </p>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    2
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3">
                    {t("apiQuickStartStep2Title")}
                  </h3>
                  <p className="text-slate-500">
                    {t("apiQuickStartStep2Desc")}
                  </p>
                </div>
                <div className="p-6 bg-white rounded-lg shadow-sm border border-slate-200">
                  <div className="inline-flex items-center justify-center mb-4 w-12 h-12 text-xl text-white bg-blue-600 font-semibold rounded-full">
                    3
                  </div>
                  <h3 className="font-bold text-slate-800 mb-3">{t("apiQuickStartStep3Title")}</h3>
                  <p className="text-slate-500">
                    {t("apiQuickStartStep3Desc")}
                  </p>
                </div>
              </div>
            </div>

            {/* SDK 使用指南 */}
            <div className="mb-16">
              <div className="flex items-center mb-6">
                <BookOpen className="w-6 h-6 mr-2 text-blue-600" />
                <h2 className="text-2xl font-bold text-slate-800">{t("apiSdkGuideTitle")}</h2>
              </div>
              <Collapse defaultActiveKey={["overview"]} className="bg-white rounded-lg shadow-sm border border-slate-200">
                <Panel header={t("apiSdkGuideOverview")} key="overview">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideOverviewContent")}</p>
                </Panel>
                <Panel header={t("apiSdkGuideFlow")} key="flow">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideFlowContent")}</p>
                </Panel>
                <Panel header={t("apiSdkGuideConfig")} key="config">
                  <p className="text-slate-600 whitespace-pre-line mb-4">{t("apiSdkGuideConfigContent")}</p>
                  <h4 className="font-bold text-slate-800 mb-2">{t("apiSdkGuideConfigExample")}</h4>
                  <pre className="bg-slate-50 p-4 rounded-lg text-sm font-mono text-slate-700 overflow-x-auto">
{`{
  "api_base_url": "https://www.stabilize-ai.com/api",
  "user_id": "user@example.com",
  "login_token": "uuid-token-from-login",
  "model_id": "model_v1"
}`}
                  </pre>
                </Panel>
                <Panel header={t("apiSdkGuideRun")} key="run">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideRunContent")}</p>
                </Panel>
                <Panel header={t("apiSdkGuideProtocol")} key="protocol">
                  <p className="text-slate-600 whitespace-pre-line mb-4">{t("apiSdkGuideProtocolContent")}</p>
                  <h4 className="font-bold text-slate-800 mb-2">Python {t("apiEndpointExampleCode")}</h4>
                  <pre className="bg-slate-900 p-4 rounded-lg text-sm text-slate-200 overflow-x-auto">
{`import socket
import json

s = socket.socket(socket.AF_INET, socket.SOCK_STREAM)
s.connect(("127.0.0.1", 9000))
fp = s.makefile("rwb")

# 1. Login
fp.write(json.dumps({"login": {"username": "admin", "password": "admin123"}}).encode() + b"\\n")
fp.flush()
print(fp.readline().decode())

# 2. Resume
fp.write(json.dumps({"resume": False}).encode() + b"\\n")
fp.flush()
fp.readline()

# 3. Inference
obs = [0.0] * 8
feedback = [0.0]
fp.write(json.dumps({"obs": obs, "feedback": feedback}).encode() + b"\\n")
fp.flush()
print(fp.readline().decode())

s.close()`}
                  </pre>
                </Panel>
                <Panel header={t("apiSdkGuideRules")} key="rules">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideRulesContent")}</p>
                </Panel>
                <Panel header={t("apiSdkGuideNotes")} key="notes">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideNotesContent")}</p>
                </Panel>
                <Panel header={t("apiSdkGuideTroubleshoot")} key="troubleshoot">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideTroubleshootContent")}</p>
                </Panel>
                <Panel header={t("apiSdkGuideAppendix")} key="appendix">
                  <p className="text-slate-600 whitespace-pre-line">{t("apiSdkGuideAppendixContent")}</p>
                </Panel>
              </Collapse>
            </div>
          </div>
        </div>
      </section>

      {/* 免费试用/CTA
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-white mb-6 text-2xl">开始使用我们的API</div>
            <div className="text-blue-100 mb-12">
              免费试用我们的API，体验强大的AI智能服务，赋能您的电子产品。
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              <Button variant="secondary" size="lg">
                <ArrowRight className="mr-2 h-5 w-5" />
                开始试用
              </Button>
            </div>
          </div>
        </div>
      </section> */}

      {/* 页脚 */}
      <Footer />
    </div>
  );
};

export default API;
